import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
    fetchIncidents,
    enrichIncidentsWithRelatedData,
} from "../services/service";
import { Incident, ApiResponse } from "../types/types";

// ============================================================================
// QUERY KEYS
// ============================================================================

export const INCIDENT_QUERY_KEYS = {
    all: ["incidents"] as const,
    list: (params: Record<string, any>) => ["incidents", "list", params] as const,
    resolved: (params: Record<string, any>) => ["incidents", "resolved", params] as const,
    department: (department: string) => ["incidents", "department", department] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Fetches incidents for the main Incidents list page (Open + Pending statuses).
 */
export function useIncidentsQuery(params: {
    currentPage: number;
    pageSize: number;
    debouncedSearchTerm: string;
    statusFilter: string;
}) {
    const { currentPage, pageSize, debouncedSearchTerm, statusFilter } = params;

    return useQuery({
        queryKey: INCIDENT_QUERY_KEYS.list({
            currentPage,
            pageSize,
            debouncedSearchTerm,
            statusFilter,
        }),
        queryFn: async () => {
            const statusesToShow = ["Open", "Pending"];
            const queryParams: any = {
                limit: pageSize,
                skip: currentPage * pageSize,
            };

            if (statusFilter && statusesToShow.includes(statusFilter)) {
                queryParams.status = statusFilter;
            } else {
                queryParams.statusIn = statusesToShow.join(",");
            }

            if (debouncedSearchTerm) {
                queryParams.search = debouncedSearchTerm;
            }

            const response = await fetchIncidents(queryParams);

            if (response.success) {
                let incidents = response.data || [];

                incidents = incidents.filter((incident: any) =>
                    statusesToShow.includes(incident.status)
                );

                // Filter out External Emergency Preparedness incidents
                incidents = incidents.filter(
                    (incident: any) =>
                        incident.department !== "External Emergency Preparedness"
                );

                const enrichedIncidents =
                    await enrichIncidentsWithRelatedData(incidents);

                const finalIncidents = enrichedIncidents.map((incident) => ({
                    ...incident,
                    hospitalName: incident.hospitalName || "",
                }));

                return {
                    incidents: finalIncidents,
                    total: response.total || finalIncidents.length,
                };
            }

            return { incidents: [] as Incident[], total: 0 };
        },
    });
}

/**
 * Fetches resolved incidents for the Reports page.
 */
export function useResolvedIncidentsQuery(params: {
    debouncedSearchTerm: string;
    selectedPremises: string;
    selectedRegion: string;
}) {
    const { debouncedSearchTerm, selectedPremises, selectedRegion } = params;

    return useQuery({
        queryKey: INCIDENT_QUERY_KEYS.resolved({
            debouncedSearchTerm,
            selectedPremises,
            selectedRegion,
        }),
        queryFn: async () => {
            const queryParams: any = {
                status: "Resolved",
            };

            if (debouncedSearchTerm) {
                queryParams.search = debouncedSearchTerm;
            }

            if (selectedPremises) {
                queryParams.hospitalName = selectedPremises;
            }

            if (selectedRegion) {
                queryParams.region = selectedRegion;
            }

            const response = await fetchIncidents(queryParams);
            const incidents = response.data || [];

            const enrichedIncidents = await enrichIncidentsWithRelatedData(incidents);

            const finalIncidents = enrichedIncidents.map((incident) => ({
                ...incident,
                hospitalName: incident.hospitalName || "",
                region: incident.region || "",
            }));

            return Array.isArray(finalIncidents) ? finalIncidents : [];
        },
    });
}

/**
 * Fetches department-specific incidents for IncidentDashboard.
 */
export function useDepartmentIncidentsQuery(
    department: string,
    fetchService: (department: string) => Promise<ApiResponse<Incident[]>>
) {
    return useQuery({
        queryKey: INCIDENT_QUERY_KEYS.department(department),
        queryFn: async () => {
            const response = await fetchService(department);
            const incidents: Incident[] = response.data || [];

            const enrichedIncidents = await enrichIncidentsWithRelatedData(incidents);

            return enrichedIncidents.map((incident) => ({
                ...incident,
                reportDate: incident.reportDate,
                incidentDate: incident.incidentDate,
                region: incident.region || "",
                hospitalName: incident.hospitalName || "",
            }));
        },
    });
}

/**
 * Returns a function that invalidates all incident queries,
 * replacing window.dispatchEvent for cross-component data sync.
 */
export function useInvalidateIncidents() {
    const queryClient = useQueryClient();

    return useCallback(() => {
        queryClient.invalidateQueries({ queryKey: INCIDENT_QUERY_KEYS.all });
    }, [queryClient]);
}
